import { useState } from "react";

import type { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { useGetTherapistEarnings } from "api/hooks/useGetTherapistEarnings";
import { useProfileContext } from "contexts/ProfileContext";
import { Pagination } from "shared/atoms/Pagination";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { useLocalizedDate } from "utils/date";

import { earnings } from "../../utils";

type EarningsActivityName =
  | "chat_notes"
  | "discharge_notes"
  | "enrollment_bonuses"
  | "follow_up"
  | "kick_off"
  | "monthly_notes"
  | "others"
  | "quarterly_follow_up"
  | "selfcare_bonus_income"
  | "six_week_bonuses"
  | "supplementary_calls"
  | "total"
  | "treatments"
  | "weekly_notes";

export const EarningsTable: React.FC = () => {
  const { t } = useTranslation();
  const { add, sub, format, set, isAfter, isBefore } = useLocalizedDate();
  const currentMonth = set(new Date(), { date: 1, hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
  const { profile } = useProfileContext();

  invariant(profile);

  const { id: therapistId, created_at: createdAt } = profile;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const { data, isLoading } = useGetTherapistEarnings({ date: selectedMonth, therapistId });

  const hasPreviousPage = isAfter(selectedMonth, new Date(createdAt ?? ""));
  const hasNextPage = isBefore(selectedMonth, currentMonth);

  const currency = data?.currency;

  const getTranslatedEarning = (amount: number | undefined, c: string | undefined) => {
    switch (c) {
      case "EUR":
        return t(`common.currency.amountInEUR`, { amount });
      case "GBP":
        return t(`common.currency.amountInGBP`, { amount });
      case "SEK":
        return t(`common.currency.amountInSEK`, { amount });
      case "USD":
        return t(`common.currency.amountInUSD`, { amount });
      default:
        return "";
    }
  };

  const getTranslatedActivityName = (name: EarningsActivityName) => {
    switch (name) {
      case "chat_notes":
        return t("dashboard.tables.earnings.rows.chat_notes");
      case "discharge_notes":
        return t("dashboard.tables.earnings.rows.discharge_notes");
      case "enrollment_bonuses":
        return t("dashboard.tables.earnings.rows.enrollment_bonuses");
      case "follow_up":
        return t("dashboard.tables.earnings.rows.follow_up");
      case "kick_off":
        return t("dashboard.tables.earnings.rows.kick_off");
      case "monthly_notes":
        return t("dashboard.tables.earnings.rows.monthly_notes");
      case "others":
        return t("dashboard.tables.earnings.rows.others");
      case "quarterly_follow_up":
        return t("dashboard.tables.earnings.rows.quarterly_follow_up");
      case "selfcare_bonus_income":
        return t("dashboard.tables.earnings.rows.selfcare_bonus_income");
      case "six_week_bonuses":
        return t("dashboard.tables.earnings.rows.six_week_bonuses");
      case "supplementary_calls":
        return t("dashboard.tables.earnings.rows.supplementary_calls");
      case "total":
        return t("dashboard.tables.earnings.rows.total");
      case "treatments":
        return t("dashboard.tables.earnings.rows.treatments");
      case "weekly_notes":
        return t("dashboard.tables.earnings.rows.weekly_notes");
      default:
        return "";
    }
  };

  const dateLabel = format(selectedMonth, "MMM yyyy");
  const rows = earnings(data);

  const totalEarnings = rows?.find(row => row.name === "total")?.earnings;
  const totalEarningsFormatted = getTranslatedEarning(totalEarnings, currency);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("dashboard.tables.earnings.columns.type"),
      valueGetter: (_value, row) => getTranslatedActivityName(row.name),
      flex: 1,
    },
    {
      field: "count",
      headerName: t("dashboard.tables.earnings.columns.count"),
    },
    {
      field: "earnings",
      headerName: t("dashboard.tables.earnings.columns.earnings"),
      valueGetter: (_value, row) => getTranslatedEarning(row.earnings, currency),
      renderCell: params => <span data-testid={`${params.row.name}-amount`}>{params.value}</span>,
    },
  ];

  return (
    <MUIDataGrid
      columns={columns}
      rows={rows || []}
      getRowId={row => row.name}
      loading={isLoading}
      title={t("dashboard.tables.earnings.title")}
      notifications={totalEarningsFormatted}
      toolbarContent={
        <Pagination
          label={dateLabel}
          pageInfo={{ hasNextPage, hasPreviousPage }}
          onPageChange={goTo => {
            setSelectedMonth(goTo === "next" ? add(selectedMonth, { months: 1 }) : sub(selectedMonth, { months: 1 }));
          }}
        />
      }
      disableColumnMenu
      disableColumnSorting
      sx={{ border: 0 }}
    />
  );
};
